import React, { useEffect, useState, FC, useCallback } from 'react';

import { Popper } from '@mui/material';
import { RenderElementProps } from 'slate-react';

import styled from 'styled-components';

// constants
import aampeTheme from '../../../../constants/aampeTheme';
import ICONS from '../../../../constants/icons';

// models
import { LinkElement } from '../../../../models/editor.model';

// helpers
import { extractLinkInfo } from '../../../../views/formulaCreate/messagePreview/email/email.helper';

// components
import ZeroWidthSpace from './ZeroWidthSpace';

type LinkElementComponentProps = RenderElementProps & {
    element: LinkElement;
    selectedElement?: { [key: string]: any };
    setIsLinkModalOpen: (isLinkModalOpen: boolean) => void;
};

const LinkElementComponent: FC<
    React.PropsWithChildren<LinkElementComponentProps>
> = ({
    element,
    attributes,
    children,
    selectedElement,
    setIsLinkModalOpen,
}) => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [showToolBar, setShowToolBar] = useState<boolean>(false);

    const selected = selectedElement
        ? selectedElement.elementId === element.id
        : false;

    const onMouseChange = (showTooltip: boolean) => {
        setShowToolBar(showTooltip);
    };

    const handleClick = useCallback(() => {
        attributes.ref.current?.click();
        setIsLinkModalOpen(true);
    }, [setIsLinkModalOpen, attributes.ref]);

    useEffect(() => {
        if (selected) {
            handleClick();
        }
    }, [handleClick, selected]);

    const updateAnchorEl = useCallback(() => {
        const htmlElement = document.getElementById(element.id);
        setAnchorEl(htmlElement);
    }, [element]);

    useEffect(() => {
        if (showToolBar) {
            updateAnchorEl();
        }
    }, [showToolBar, updateAnchorEl]);

    return (
        <>
            <ZeroWidthSpace />
            <Link
                id={element.id}
                tabIndex={0}
                {...attributes}
                onMouseEnter={() => onMouseChange(true)}
                onMouseLeave={() => onMouseChange(false)}
            >
                <ZeroWidthSpace />
                <span>
                    {extractLinkInfo(element.children[0].text).textValue}
                </span>
                {children}
            </Link>
            <Popper
                open={!!(anchorEl && showToolBar)}
                anchorEl={anchorEl}
                placement="top"
                onMouseEnter={() => {
                    onMouseChange(true);
                }}
                onMouseLeave={() => onMouseChange(false)}
            >
                <Container>
                    <AddButton onClick={handleClick}>
                        <Icon />
                        <ButtonText>Edit link</ButtonText>
                    </AddButton>
                </Container>
            </Popper>
        </>
    );
};

export default LinkElementComponent;

const Link = styled.span(() => ({
    outline: 'none',
    display: 'inline-block',
    fontWeight: 500,
    userSelect: 'auto',
    lineHeight: '22px',
    minWidth: '10px',
    color: aampeTheme.colors.solidA01,
    borderBottom: `1px solid`,
    cursor: 'pointer',
}));

const Container = styled.div({
    padding: aampeTheme.spacing(0, 1),
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing(1),
    border: `1px solid ${aampeTheme.colors.bordersWG01}`,
    borderRadius: aampeTheme.borderRadius.default,
    backgroundColor: aampeTheme.colors.white,
    boxShadow:
        '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)',
});

const AddButton = styled.div({
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    color: aampeTheme.colors.gray600,
});

const ButtonText = styled.span({
    lineHeight: '18px',
    fontSize: '12px',
});

const Icon = styled(ICONS.editPencil)(() => ({
    fontWeight: 600,
    height: '20px',
}));
